import React, { Component } from 'react';
import { connect } from 'react-redux';
import Collapse from '../../common/Collapse';
import * as T from '../../common/Typography';
import * as Yup from 'yup';
import { message } from 'antd';

import { fetchSessionDetails } from '../../../actions/groupSessionsAction';
import {
  updateSessionAttendeesList as updateSessionAttendeesListAction,
  cancelScheduledEmail as cancelScheduledEmailAction
} from '../../../actions/sessionAction';
import {
  checkTrainerReflectionExists as checkTrainerReflectionExistsAction,
  resetTrainerReflection as resetTrainerReflectionAction
} from '../../../actions/trainerReflectionActions';

import { splitEmailsList } from '../../../helpers';

// ANTD COMPONENTS
import Spin from '../../common/Spin';
import Icon from '../../common/Icon';

// COMMON COMPONENTS
import Layout from '../../Layouts';

// STYLING
import * as S from './SessionDetails.Style';

// SUB COMPONENTS
import SessionTopDetails from './SessionTopDetails';
import SessionActions from './SessionActions';
import SessionSurveys from './SessionSurveys';
import ManageAttendees from './ManageAttendees';
import InviteAndPromote from './InviteAndPromote';
import DrawerContent from './DrawerContent';
import TrainerReflection from './TrainerReflection';

// to validate emails
const emailSchema = Yup.string()
  .email()
  .required();

const isTrainerOnSession = (session, userId) =>
  (session.trainers || []).some(t => String(t._id) === String(userId));

class SessionDetails extends Component {
  state = {
    openSection: '1',
    visible: false,
    drawerKey: null,
    emailId: null,
    viewParticipants: false
  };

  selectRef = React.createRef();

  componentDidMount() {
    if (window.ClipboardEvent) {
      let dT = null;
      try {
        dT = new DataTransfer();
      } catch (e) {
        // ignore the error
      }
      const evt = new ClipboardEvent('paste', { clipboardData: dT });
      if (evt.clipboardData || window.clipboardData) {
        (evt.clipboardData || window.clipboardData).setData('text/plain', '');
        document.addEventListener('paste', this.pasteEmails);
        document.dispatchEvent(evt);
      }
    }

    const { id } = this.props.match.params;
    // call action and pass it the id of session to fetch it's details
    this.props.fetchSessionDetails(id, this.props.history);
  }

  componentDidUpdate() {
    const { lastUpdate } = this.state;
    const {
      sessionDetails,
      userId,
      checkTrainerReflectionExists,
      resetTrainerReflection
    } = this.props;
    if (lastUpdate !== sessionDetails.updatedAt) {
      this.setListIntoState();
    }

    const isSessionTrainer = isTrainerOnSession(sessionDetails, userId);
    if (
      sessionDetails._id &&
      isSessionTrainer &&
      this.feedbackCheckedId !== sessionDetails._id
    ) {
      this.feedbackCheckedId = sessionDetails._id;
      resetTrainerReflection();
      checkTrainerReflectionExists(sessionDetails._id);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('paste', this.pasteEmails);
  }

  callback = key => {
    this.setState({ openSection: key });
  };

  setListIntoState = () => {
    const { sessionDetails = {} } = this.props;
    const { participantsEmails = [], sentEmails = [] } = sessionDetails;
    const newEmails = participantsEmails
      .filter(item => {
        return item.status === 'new';
      })
      .map(item => item.email);

    const sentEmailsList = participantsEmails
      .filter(item => item.status === 'sent')
      .map(item => item.email);

    const confirmedEmails = participantsEmails
      .filter(item => item.status === 'confirmed')
      .map(item => item.email);

    const reminderEmails = sentEmails.filter(item => item.type === 'reminder');

    this.setState({
      newEmails: [...newEmails, ...sentEmailsList],
      confirmedEmails,
      lastUpdate: sessionDetails.updatedAt,
      reminderEmails
    });
  };

  // open drawer
  handleDrawerOpen = e => {
    const { key, surveyType, emailId } = e.target.dataset;

    this.setState({
      visible: true,
      drawerKey: key,
      emailId,
      surveyType
    });
  };

  // update the attendees list status = (new || confirmed)
  handleUpdateAttendees = (values, status, blur) => {
    const validEmails = [];
    values.forEach(item => {
      try {
        const validEmail = emailSchema.validateSync(
          item.replace(/[, ;"']/g, '')
        );

        if (
          validEmail &&
          item &&
          !validEmails.includes(item.replace(/[, ;"']/g, '')) &&
          !validEmails.includes(item)
        ) {
          validEmails.push(
            item
              .replace(/[, ;"']/g, '')
              .toLowerCase()
              .trim()
          );
        }
      } catch (err) {
        if (!blur && item.split('@').length < 3) {
          return message.error(`Invalid!, ${err?.errors[0]}`);
        }
      }
    });

    // { newEmails||confirmedEmails: ["emails"]}
    this.setState({ [`${status}Emails`]: validEmails }, () => {
      if (blur) {
        const select = this.selectRef.current;
        select.blur();
        setTimeout(() => {
          select.focus();
        }, 200);
      }
    });
  };

  onTypingEmails = (value, status) => {
    const { [`${status}Emails`]: oldEmails } = this.state;
    if (
      value &&
      (value.includes(' ') || value.includes(',') || value.includes(';'))
    ) {
      const splittedEmails = splitEmailsList(value);

      // get latest added email
      const latestEmail =
        splittedEmails && splittedEmails[splittedEmails.length - 1];

      if (latestEmail?.includes('@') && latestEmail?.includes('.')) {
        this.handleUpdateAttendees(
          [...splittedEmails, ...oldEmails],
          status,
          true
        );
      } else {
        this.handleUpdateAttendees(
          [
            ...splittedEmails.slice(0, splitEmailsList.length - 1),
            ...oldEmails
          ],
          status,
          true
        );
      }
    }
  };

  // submit the updated emails
  handleSubmitUpdateAttendees = status => {
    const { confirmedEmails, newEmails, nextKey } = this.state;
    const emailsArray = status === 'new' ? newEmails : confirmedEmails;
    const { sessionDetails, updateSessionAttendeesList } = this.props;
    const participantsEmails = emailsArray.map(item => ({
      email: item.toLowerCase().trim(),
      status
    }));

    const callback = nextKey ? this.openNextKey : this.handleCloseDrawer;

    updateSessionAttendeesList({
      sessionId: sessionDetails._id,
      participantsEmails,
      status,
      handleCloseDrawer: callback,
      history: this.props.history
    });
  };

  openNextKey = () => {
    const { nextKey } = this.state;
    this.setState({
      visible: true,
      drawerKey: nextKey
    });
  };

  onCopy = status => {
    const { newEmails, confirmedEmails } = this.state;
    let dataForCopy;
    switch (status) {
      case 'new':
        dataForCopy = newEmails;
        break;

      case 'confirmed':
        dataForCopy = confirmedEmails;
        break;

      default:
        dataForCopy = [];
        break;
    }

    if (dataForCopy.length) {
      this.setState({ dataForCopy: dataForCopy.join(';') }, () => {
        if (dataForCopy.length) {
          const copyText = document.getElementById('dataForCopy');
          let range;
          let selection;
          if (document.body.createTextRange) {
            range = document.body.createTextRange();
            range.moveToElementText(copyText);

            range.select();
          } else if (window.getSelection) {
            selection = window.getSelection();
            range = document.createRange();
            range.selectNodeContents(copyText);
            selection.removeAllRanges();
            selection.addRange(range);
          }

          try {
            document.execCommand('copy');
            message.success('copied');
          } catch (err) {
            // console.log(err);
          }
        }
      });
    }
  };

  // to clear the emails list
  onClear = status => {
    this.setState({ [`${status}Emails`]: [] });
  };

  // blur handler for Select component
  onSelectBlur = () => {
    this.setState({ focused: false });
  };

  // focus handler for Select component
  onSelectFocus = () => {
    this.setState({ focused: true });
  };

  // when pasting list of emails in the Select component
  pasteEmails = event => {
    const { focused, drawerKey } = this.state;

    let status = '';
    switch (drawerKey) {
      case 'view-invitees':
        status = 'new';
        break;

      case 'viewAttendeesList':
        status = 'confirmed';
        break;

      default:
        break;
    }

    let emailsArray;

    if (focused) {
      event.preventDefault();
      const pastedString = event.clipboardData.getData('text/plain');

      emailsArray = splitEmailsList(pastedString);

      const { [`${status}Emails`]: oldEmails } = this.state;

      this.handleUpdateAttendees([...emailsArray, ...oldEmails], status);
    }
  };

  // close the drawer
  handleCloseDrawer = () => {
    this.setState({
      visible: false,
      drawerKey: null,
      nextKey: null
    });
    this.setListIntoState();
  };

  // to handle moving from the edit email page to add attendees page
  handleAddEmailsClick = (drawerKey, nextKey) => {
    this.setState({
      visible: true,
      drawerKey,
      nextKey
    });
  };

  handleCancelEmail = emailId => {
    const { cancelScheduledEmail, sessionDetails } = this.props;

    cancelScheduledEmail(
      {
        sessionId: sessionDetails._id,
        scheduledEmailId: emailId
      },
      this.props.history
    );
  };
  setViewParticipants = hide => {
    this.setState({
      viewParticipants: !hide
    });
  };
  render() {
    const { sessionDetails, name, userId, trainerReflectionState } = this.props;
    const {
      drawerKey,
      loading,
      visible,
      confirmedEmails,
      reminderEmails,
      newEmails,
      surveyType,
      dataForCopy,
      emailId,
      viewParticipants
    } = this.state;
    if (!sessionDetails) {
      return Spin;
    }
    const { participantsDidNotAttendPrevMods } = sessionDetails;

    const isSessionTrainer = isTrainerOnSession(sessionDetails, userId);
    const hasFeedback =
      trainerReflectionState.submitted ||
      !!trainerReflectionState.existingResponse;

    const feedbackItem = isSessionTrainer && {
      title: (
        <S.FeedbackHeaderDiv>
          Give Feedback
          {!hasFeedback && <S.IncompleteBadge>Incomplete</S.IncompleteBadge>}
        </S.FeedbackHeaderDiv>
      ),
      content: <TrainerReflection sessionDetails={sessionDetails} />,
      key: 5
    };

    return (
      <Layout>
        <S.SessionDetailsWrapper>
          <T.H1 mb="7">Manage Session</T.H1>{' '}
          {!!participantsDidNotAttendPrevMods?.length && (
            <S.WarningWrapper>
              <S.WarningText>
                <S.IconWrapper>
                  <Icon icon="warning" />
                </S.IconWrapper>
                <div>
                  {participantsDidNotAttendPrevMods.length > 1
                    ? `You have ${participantsDidNotAttendPrevMods.length} people
                  attending who have not yet completed the previous module.
                  Please contact them to ensure they will complete the module
                  before your session. Otherwise, remove them from the attendee list.`
                    : `You have ${participantsDidNotAttendPrevMods.length} person
                  attending who has not yet completed the previous module.
                  Please contact them to ensure they will complete the module
                  before your session. Otherwise, remove them from the attendee list.`}
                  <br />
                  {!viewParticipants && (
                    <T.Link
                      color="secondary"
                      onClick={() => this.setViewParticipants()}
                    >
                      View participants
                    </T.Link>
                  )}
                </div>
              </S.WarningText>
              {viewParticipants && (
                <S.EmailList>
                  {participantsDidNotAttendPrevMods.map(p => (
                    <li>
                      <T.Link
                        external
                        href={`mailto:${p.email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="secondary"
                      >
                        {p.email}
                      </T.Link>
                    </li>
                  ))}
                </S.EmailList>
              )}
            </S.WarningWrapper>
          )}
          <Collapse
            onChange={() => this.setViewParticipants(true)}
            items={[
              {
                title: <S.HeaderDiv>Event Details</S.HeaderDiv>,
                content: (
                  <>
                    <SessionTopDetails
                      sessionDetails={sessionDetails}
                      trainerView
                    />
                    <SessionActions
                      sessionDetails={sessionDetails}
                      history={this.props.history}
                    />
                  </>
                ),
                key: 1
              },
              {
                title: <S.HeaderDiv>Invite & Promote</S.HeaderDiv>,
                content: (
                  <InviteAndPromote
                    sessionDetails={sessionDetails}
                    handleDrawerOpen={this.handleDrawerOpen}
                  />
                ),
                key: 2
              },
              {
                title: <S.HeaderDiv>Manage Attendees</S.HeaderDiv>,
                content: (
                  <ManageAttendees
                    sessionDetails={sessionDetails}
                    handleDrawerOpen={this.handleDrawerOpen}
                    confirmedAttendeesList={confirmedEmails}
                  />
                ),
                key: 3
              },
              {
                title: <S.HeaderDiv>Get Feedback</S.HeaderDiv>,
                content: (
                  <SessionSurveys
                    sessionDetails={sessionDetails}
                    handleDrawerOpen={this.handleDrawerOpen}
                  />
                ),
                key: 4
              },
              feedbackItem
            ].filter(Boolean)}
          />
          <div
            style={{ width: '100%', position: 'absolute', left: 0 }}
            id="parentDiv"
          >
            <S.Drawer
              placement="left"
              width="100%"
              height="100%"
              onClose={this.handleCloseDrawer}
              visible={visible}
              closable
              bodyStyle={{
                minHeight: '100%'
              }}
              getContainer="#parentDiv"
              destroyOnClose
            >
              <S.DrawerWrapper>
                <S.BackWrapper onClick={this.handleCloseDrawer}>
                  <Icon
                    icon="chevron"
                    direction="left"
                    width="16"
                    height="16"
                    mr="2"
                    color="primary"
                  />
                  <T.P color="darkGray" weight={500}>
                    Back
                  </T.P>
                </S.BackWrapper>
                <DrawerContent
                  // All
                  drawerKey={drawerKey}
                  loading={loading}
                  sessionDetails={sessionDetails}
                  onSelectBlur={this.onSelectBlur}
                  onSelectFocus={this.onSelectFocus}
                  name={name}
                  onCopy={this.onCopy}
                  onClear={this.onClear}
                  handleCloseDrawer={this.handleCloseDrawer}
                  // update
                  handleSubmitUpdateAttendees={this.handleSubmitUpdateAttendees}
                  confirmedAttendeesList={confirmedEmails}
                  handleUpdateAttendees={this.handleUpdateAttendees}
                  // sendEmails
                  handleAddEmailsClick={this.handleAddEmailsClick}
                  // email list
                  reminderEmails={reminderEmails}
                  handleDrawerOpen={this.handleDrawerOpen}
                  // special requirements
                  specialRequirements={sessionDetails.specialRequirements}
                  //
                  newAndSentEmailsList={newEmails}
                  // feedback
                  handleCancelEmail={this.handleCancelEmail}
                  surveyType={surveyType}
                  handleSubmitSchedule={this.handleSubmitSchedule}
                  emailId={emailId}
                  onTypingEmails={this.onTypingEmails}
                  selectRef={this.selectRef}
                />
              </S.DrawerWrapper>
            </S.Drawer>
          </div>
          <div
            id="dataForCopy"
            style={{
              opacity: '0',
              position: 'absolute',
              width: '0',
              hieght: '0',
              // to prevent Y scroll
              left: '-100000rem'
            }}
          >
            {dataForCopy}
          </div>
        </S.SessionDetailsWrapper>
      </Layout>
    );
  }
}

const mapStateToProps = state => {
  return {
    sessionDetails: state.sessions.sessionDetails,
    name: state.auth.name,
    userId: state.auth.userId,
    trainerReflectionState: state.trainerReflection
  };
};
export default connect(mapStateToProps, {
  fetchSessionDetails,
  updateSessionAttendeesList: updateSessionAttendeesListAction,
  cancelScheduledEmail: cancelScheduledEmailAction,
  checkTrainerReflectionExists: checkTrainerReflectionExistsAction,
  resetTrainerReflection: resetTrainerReflectionAction
})(SessionDetails);
